import {
  Autocomplete,
  DataGrid,
  FilterToolbar,
  Form,
  useNotifications
} from '@ijoin/components';
import {
  AdminLinkedMapRes,
  useDeleteAdminAdvisoryOrganizationManagedAccountProvider,
  useDeleteManagedAccountMethodologyAdvisoryOrganization,
  useGetAdminAdvisoryOrganizationManagedAccountProviders,
  useGetAdminAdvisoryOrganizationMethodologies,
  useGetAdminAdvisoryOrganizationUnlinkedManagedAccountProviders,
  useGetAdminAdvisoryOrganizationUnlinkedMethodologies,
  usePostAdminAdvisoryOrganizationManagedAccountProvider,
  usePostAdminAdvisoryOrganizationMethodology
} from '@ijoin/hooks';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { camelCase, orderBy } from 'lodash';
import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { errorMessage } from '../helpers';
import { LinkButton } from './LinkButton';
import { MethodologyActionsCell } from './MethodologyActionsCell';

type MethodologyOption = { label: string; value: string };

export const ManagedAccountsTab: FC<{
  advisoryOrganizationId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methodologies = useGetAdminAdvisoryOrganizationMethodologies(
    props.advisoryOrganizationId,
    {
      query: {
        select: data => orderBy(data, 'managedAccountMethodologyName', 'asc')
      }
    }
  );
  const unlinkedMethodologies =
    useGetAdminAdvisoryOrganizationUnlinkedMethodologies(
      props.advisoryOrganizationId
    );
  const maps = useGetAdminAdvisoryOrganizationManagedAccountProviders(
    props.advisoryOrganizationId
  );
  const unlinkedMaps =
    useGetAdminAdvisoryOrganizationUnlinkedManagedAccountProviders(
      props.advisoryOrganizationId
    );

  const linkForm = useForm<{ methodology: MethodologyOption | null }>({
    defaultValues: { methodology: null }
  });
  const methodologyToLink = linkForm.watch('methodology');

  const providerLinkForm = useForm<{ provider: MethodologyOption | null }>({
    defaultValues: { provider: null }
  });
  const providerToLink = providerLinkForm.watch('provider');

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey.some(
          key =>
            typeof key === 'string' &&
            key.startsWith(
              `/api/v3/admin/advisory-organizations/${props.advisoryOrganizationId}`
            )
        )
    });
  };

  const linkMethodology = usePostAdminAdvisoryOrganizationMethodology({
    mutation: {
      onError: () => notifications.error('Failed to link methodology'),
      onSuccess: () => {
        invalidate();
        linkForm.setValue('methodology', null);
        notifications.success('Methodology linked');
      }
    }
  });
  const unlinkMethodology =
    useDeleteManagedAccountMethodologyAdvisoryOrganization({
      mutation: {
        onError: err =>
          notifications.error(
            errorMessage(err, 'Failed to unlink methodology')
          ),
        onSuccess: () => {
          invalidate();
          notifications.success('Methodology unlinked');
        }
      }
    });

  const linkProvider = usePostAdminAdvisoryOrganizationManagedAccountProvider({
    mutation: {
      onError: () =>
        notifications.error('Failed to link managed account provider'),
      onSuccess: () => {
        invalidate();
        providerLinkForm.setValue('provider', null);
        notifications.success('Managed account provider linked');
      }
    }
  });
  const unlinkProvider =
    useDeleteAdminAdvisoryOrganizationManagedAccountProvider({
      mutation: {
        onError: () =>
          notifications.error('Failed to unlink managed account provider'),
        onSuccess: () => {
          invalidate();
          notifications.success('Managed account provider unlinked');
        }
      }
    });

  const onLinkMethodology = () => {
    if (!methodologyToLink) return;
    linkMethodology.mutate({
      advisoryOrganizationId: props.advisoryOrganizationId,
      managedAccountMethodologyId: methodologyToLink.value
    });
  };

  const onLinkProvider = () => {
    if (!providerToLink) return;
    linkProvider.mutate({
      advisoryOrganizationId: props.advisoryOrganizationId,
      managedAccountProviderId: providerToLink.value
    });
  };

  const methodologyColumns: GridColDef[] = [
    {
      field: 'managedAccountMethodologyName',
      flex: 2,
      headerName: 'Name'
    },
    {
      field: 'managedAccountMethodologyId',
      flex: 1,
      headerName: 'Methodology ID'
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => {
        const usedByCount = (maps.data ?? []).filter(
          provider =>
            provider.managedAccountMethodologyId ===
            params.row.managedAccountMethodologyId
        ).length;
        const name = params.row.managedAccountMethodologyName;

        return (
          <MethodologyActionsCell
            message={
              usedByCount
                ? `${usedByCount} linked ${usedByCount === 1 ? 'provider uses' : 'providers use'} ${name} and will be unlinked from this advisory organization too. Advisors will no longer be able to select ${name} or ${usedByCount === 1 ? 'that provider' : 'those providers'}. Unlink ${name}?`
                : `Unlink ${name} from this advisory organization? Advisors will no longer be able to select it for new providers.`
            }
            name={name}
            onUnlink={() =>
              unlinkMethodology.mutate({
                advisoryOrganizationId: props.advisoryOrganizationId,
                managedAccountMethodologyId:
                  params.row.managedAccountMethodologyId
              })
            }
          />
        );
      },
      sortable: false,
      width: 65
    }
  ];

  const mapColumns: GridColDef<AdminLinkedMapRes>[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Description',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.description)}
          params={{
            managedAccountProviderId: params.row.managedAccountProviderId
          }}
          to='/managed-account-providers/$managedAccountProviderId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'setupComplete',
      flex: 1,
      headerName: 'Setup Complete',
      renderCell: params => (params.value ? 'Yes' : 'No')
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <MethodologyActionsCell
          name={params.row.description}
          onUnlink={() =>
            unlinkProvider.mutate({
              advisoryOrganizationId: props.advisoryOrganizationId,
              managedAccountProviderId: params.row.managedAccountProviderId
            })
          }
          title='Unlink Managed Account Provider'
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Stack spacing={1}>
        <Stack alignItems='center' direction='row' spacing={0.5}>
          <Typography variant='h6'>Advisor-Selectable Methodologies</Typography>
          <Tooltip title='Controls which methodologies advisors in this organization can select when creating or editing managed account providers. Methodologies define the investment strategy (e.g. asset allocation approach) used by a provider. Unlinking does not affect existing providers.'>
            <IconButton data-testid='methodologiesHelp'>
              <HelpOutlineIcon color='action' />
            </IconButton>
          </Tooltip>
        </Stack>
        <Form methods={linkForm}>
          <Stack
            alignItems='flex-end'
            direction='row'
            spacing={1}
            sx={{ mb: 2 }}>
            <Autocomplete<MethodologyOption>
              data-testid='methodologyToLink'
              hideError
              label='Methodology'
              name='methodology'
              options={(unlinkedMethodologies.data ?? []).map(m => ({
                label: m.managedAccountMethodologyName,
                value: m.managedAccountMethodologyId
              }))}
              sx={{ minWidth: 300 }}
            />
            <Tooltip title='Link'>
              <IconButton
                data-testid='linkMethodology'
                disabled={!methodologyToLink}
                onClick={onLinkMethodology}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Form>
        <DataGrid
          columns={methodologyColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.managedAccountMethodologyId}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          loading={methodologies.isFetching}
          pageSizeOptions={[5, 10, 25]}
          rows={methodologies.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedMethodologies'
            }
          }}
        />
      </Stack>

      <Stack spacing={1}>
        <Stack alignItems='center' direction='row' spacing={0.5}>
          <Typography variant='h6'>Linked Managed Account Providers</Typography>
          <Tooltip title='Controls which managed account providers are available to this advisory organization. Providers are created and edited on the Managed Account Providers page.'>
            <IconButton data-testid='linkedProvidersHelp'>
              <HelpOutlineIcon color='action' />
            </IconButton>
          </Tooltip>
        </Stack>
        <Form methods={providerLinkForm}>
          <Stack
            alignItems='flex-end'
            direction='row'
            marginBottom={2}
            spacing={1}>
            <Autocomplete<MethodologyOption>
              data-testid='providerToLink'
              hideError
              label='Provider'
              name='provider'
              options={(unlinkedMaps.data ?? []).map(provider => ({
                label: provider.description,
                value: provider.managedAccountProviderId
              }))}
              sx={{ minWidth: 300 }}
            />
            <Tooltip title='Link'>
              <IconButton
                data-testid='linkManagedAccountProvider'
                disabled={!providerToLink}
                onClick={onLinkProvider}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Form>
        <DataGrid
          columns={mapColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.managedAccountProviderId}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          loading={maps.isFetching}
          pageSizeOptions={[5, 10, 25]}
          rows={maps.data ?? []}
          showColumnMenu
          showToolbar
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedManagedAccountProviders'
            },
            toolbar: {
              onRefresh: maps.refetch
            }
          }}
          slots={{ toolbar: FilterToolbar }}
        />
      </Stack>
    </Stack>
  );
};

ManagedAccountsTab.displayName = 'ManagedAccountsTab';
